import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Landmark, KeyRound, Link2, RefreshCw, Trash2, FlaskConical, CheckCircle2, AlertTriangle, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935] h-9 text-xs";
const STATUS = {
  linked: { label: "Connectée", color: "#22c55e", icon: CheckCircle2 },
  pending: { label: "En attente du consentement", color: "#f7c935", icon: Clock },
  expired: { label: "Consentement expiré", color: "#ef4444", icon: AlertTriangle },
  rejected: { label: "Refusée", color: "#ef4444", icon: AlertTriangle },
  suspended: { label: "Suspendue", color: "#ef4444", icon: AlertTriangle },
};

function CredentialsForm({ api, societeId, onSaved }) {
  const [secretId, setSecretId] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!secretId.trim() || !secretKey.trim()) return toast.error("Secret ID et Secret Key requis");
    setBusy(true);
    try {
      await api.put("/accounting/bank/gocardless/credentials", { societe_id: societeId, secret_id: secretId.trim(), secret_key: secretKey.trim() });
      toast.success("Clés GoCardless vérifiées et enregistrées");
      setSecretId(""); setSecretKey("");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="gc-credentials-form">
      <p className="text-[11px] text-slate-500">Créez un « User secret » sur <a href="https://bankaccountdata.gocardless.com/user-secrets/" target="_blank" rel="noreferrer" className="text-[#f7c935] hover:underline inline-flex items-center gap-1">bankaccountdata.gocardless.com <ExternalLink className="w-3 h-3" /></a> puis collez-le ici. Les clés sont chiffrées et ne quittent jamais le serveur.</p>
      <Input placeholder="Secret ID" value={secretId} onChange={(e) => setSecretId(e.target.value)} className={`${inputCls} font-mono`} data-testid="gc-secret-id-input" />
      <Input placeholder="Secret Key" type="password" value={secretKey} onChange={(e) => setSecretKey(e.target.value)} className={`${inputCls} font-mono`} data-testid="gc-secret-key-input" />
      <button onClick={save} disabled={busy} className="w-full bg-[#f7c935] hover:bg-[#e6b825] text-[#0b0811] font-bold text-xs h-9 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="gc-save-credentials-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Enregistrer et vérifier les clés
      </button>
    </div>
  );
}

function ConnectForm({ api, societeId, sandboxId }) {
  const [institutions, setInstitutions] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(sandboxId);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get("/accounting/bank/gocardless/institutions", { params: { societe_id: societeId, country: "FR" } })
      .then((r) => setInstitutions(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societeId]);
  const filtered = institutions.filter((i) => !q || (i.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 60);
  const connect = async () => {
    const inst = institutions.find((i) => i.id === selected);
    if (!inst) return toast.error("Choisissez une banque");
    setBusy(true);
    try {
      const r = await api.post("/accounting/bank/gocardless/connect", { societe_id: societeId, institution_id: inst.id, institution_name: inst.name, institution_logo: inst.logo || "" });
      toast.success("Redirection vers la banque pour le consentement…");
      window.location.assign(r.data.link);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="gc-connect-form">
      <Input placeholder="Rechercher une banque (FR)…" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls} data-testid="gc-institution-search" />
      {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-500" /> : (
        <select value={selected} onChange={(e) => setSelected(e.target.value)} className={`${inputCls} w-full rounded-lg px-2`} data-testid="gc-institution-select">
          {filtered.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
      )}
      <button onClick={connect} disabled={busy || loading} className="w-full bg-[#38bdf8] hover:bg-[#0ea5e9] text-[#0b0811] font-bold text-xs h-9 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="gc-connect-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Connecter la banque
      </button>
    </div>
  );
}

function ConnectionRow({ c, onSync, onDelete, busy }) {
  const s = STATUS[c.status] || STATUS.pending;
  const Icon = s.icon;
  const bal = Object.values(c.balances || {})[0];
  return (
    <div className="border border-white/5 rounded-lg p-2.5 bg-[#14101c]" data-testid={`gc-connection-${c.connection_id}`}>
      <div className="flex items-center gap-2">
        {c.institution_logo ? <img src={c.institution_logo} alt="" className="w-6 h-6 rounded bg-white object-contain" /> : <Landmark className="w-5 h-5 text-slate-500" />}
        <div className="min-w-0 flex-1">
          <div className="text-xs text-white font-semibold truncate">{c.institution_name}{c.provider === "simulation" && <span className="ml-1 text-[9px] text-purple-400 font-black">SIMULATION</span>}</div>
          <div className="text-[10px] flex items-center gap-1" style={{ color: s.color }} data-testid={`gc-connection-status-${c.connection_id}`}><Icon className="w-3 h-3" /> {s.label}</div>
        </div>
        <button onClick={() => onSync(c)} disabled={!!busy} title="Synchroniser maintenant" className="text-[#38bdf8] hover:text-white p-1.5 rounded hover:bg-white/5 disabled:opacity-50" data-testid={`gc-sync-${c.connection_id}`}>
          {busy === c.connection_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => onDelete(c)} disabled={!!busy} title="Supprimer" className="text-red-400 hover:text-red-300 p-1.5 rounded hover:bg-white/5" data-testid={`gc-delete-${c.connection_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      {(c.accounts || []).map((a) => <div key={a.account_id} className="text-[10px] text-slate-500 font-mono mt-1 truncate">{a.iban || a.account_id}{a.name ? ` · ${a.name}` : ""}</div>)}
      <div className="text-[10px] text-slate-500 mt-1 flex justify-between gap-2">
        <span>{c.last_sync_at ? `Synchro ${new Date(c.last_sync_at).toLocaleString("fr-FR")}` : "Jamais synchronisée"}{c.last_sync_stats?.lines_new != null ? ` · ${c.last_sync_stats.lines_new} nouvelle(s) ligne(s)` : ""}</span>
        {bal && <span className="font-mono text-slate-300">{Number(bal.amount).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} {bal.currency || "€"}</span>}
      </div>
      {c.last_error && <div className="text-[10px] text-red-400 mt-1 truncate" title={c.last_error}>⚠ {c.last_error}</div>}
    </div>
  );
}

export function BankConnectionsPanel({ api, societeId, onSynced }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showKeys, setShowKeys] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try { const r = await api.get("/accounting/bank/gocardless/status", { params: { societe_id: societeId } }); setStatus(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [societeId]);

  useEffect(() => {
    const ref = searchParams.get("gc_ref");
    if (!ref) return;
    (async () => {
      try {
        const r = await api.post("/accounting/bank/gocardless/callback", null, { params: { ref } });
        toast.success(r.data.status === "linked" ? `Banque connectée — ${r.data.lines_new || 0} ligne(s) importée(s)` : "Consentement en attente de validation par la banque");
        await load();
        onSynced?.();
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
      searchParams.delete("gc_ref");
      setSearchParams(searchParams, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = async (c) => {
    setBusy(c.connection_id);
    try {
      const r = await api.post(`/accounting/bank/gocardless/connections/${c.connection_id}/sync`);
      const a = r.data.auto || {};
      toast.success(`${r.data.lines_new || 0} nouvelle(s) ligne(s) · ${(a.matched || 0) + (a.validated || 0)} pointée(s) · ${a.to_review || 0} à vérifier`);
      await load(); onSynced?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(null);
  };
  const simulate = async () => {
    setBusy("sim");
    try {
      const r = await api.post("/accounting/bank/gocardless/simulate", null, { params: { societe_id: societeId } });
      const a = r.data.auto || {};
      toast.success(`Simulation : ${r.data.lines_new} ligne(s) générée(s) · ${(a.matched || 0) + (a.validated || 0)} pointée(s) auto · ${a.to_review || 0} à vérifier`);
      await load(); onSynced?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(null);
  };
  const del = async (c) => {
    if (!window.confirm(`Supprimer la connexion « ${c.institution_name} » ? Les lignes déjà importées sont conservées.`)) return;
    try { await api.delete(`/accounting/bank/gocardless/connections/${c.connection_id}`); toast.success("Connexion supprimée"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const removeKeys = async () => {
    if (!window.confirm("Supprimer les clés GoCardless de cette société ?")) return;
    try { await api.delete("/accounting/bank/gocardless/credentials", { params: { societe_id: societeId } }); toast.success("Clés supprimées"); setShowKeys(false); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (!status) return <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div>;
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3 space-y-3" data-testid="bank-connections-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5 text-[#38bdf8]" /> Synchronisation bancaire</h4>
        {status.configured ? (
          <button onClick={() => setShowKeys(!showKeys)} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-bold flex items-center gap-1" data-testid="gc-keys-badge"><KeyRound className="w-3 h-3" /> GoCardless {status.secret_id_masked}</button>
        ) : <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 font-bold" data-testid="gc-keys-missing">Clés non configurées</span>}
      </div>
      {(!status.configured || showKeys) && (
        <div className="space-y-2">
          <CredentialsForm api={api} societeId={societeId} onSaved={() => { setShowKeys(false); load(); }} />
          {status.configured && <button onClick={removeKeys} className="text-[10px] text-red-400 hover:text-red-300" data-testid="gc-remove-keys-btn">Supprimer les clés</button>}
        </div>
      )}
      {status.configured && !showKeys && <ConnectForm api={api} societeId={societeId} sandboxId={status.sandbox_institution_id} />}
      <div className="space-y-2">
        {status.connections.map((c) => <ConnectionRow key={c.connection_id} c={c} onSync={sync} onDelete={del} busy={busy} />)}
        {status.connections.length === 0 && <div className="text-[11px] text-slate-500 text-center py-2">Aucune banque connectée. Les relevés seront synchronisés chaque jour et pointés automatiquement.</div>}
      </div>
      <button onClick={simulate} disabled={!!busy} className="w-full border border-purple-500/30 text-purple-300 hover:bg-purple-500/10 font-bold text-xs h-9 rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="gc-simulate-btn">
        {busy === "sim" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />} Simuler une synchro (banque de test)
      </button>
    </div>
  );
}
